import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styles from "./styles.module.less";
import { Button, Divider, message } from "antd";
import { useTranslation } from "react-i18next";
import { ConfigCard } from "@/components/ConfigCard";
import { EditOutlined } from "@ant-design/icons";
import { FieldConfig, SimpleForm } from "@/components/SimpleForm";
import { FormInstance } from "antd/es/form";
import moment from "moment";
import { useAsyncFn } from "react-use";
import { SaveOutlined } from "@ant-design/icons";
import { cloneDeep, isEqual } from "lodash";
import { useMediaSize } from "@/hooks/useMediaSize";
import { RightOutlined } from "@ant-design/icons";
import { MediaEditForm } from "./mediaEditForm";
import { User } from "authing-js-sdk";

export interface TempFieldConfig extends FieldConfig {
  unRender?: Boolean;
}

export const UserPageBasic = (props: any) => {
  const formRef = useRef<FormInstance>(null);

  const { t } = useTranslation();

  const [editing, setEditing] = useState(false);

  const [user, serUser] = useState(window.__user__);

  const { isPhoneMedia } = useMediaSize();

  const [mediaItemKey, setMediaItemKey] = useState(null);

  const [isModalVisible, setIsModalVisible] = useState(false);

  const genderOptions = (key: string) => {
    const options = [
      { label: t("common.unknow"), value: "U" },
      { label: t("common.man"), value: "M" },
      { label: t("common.female"), value: "W" },
    ];
    const findOption = options.find((item) => item.value === key);
    if (findOption) {
      return findOption.label;
    } else {
      return "-";
    }
  };

  const fieldConfig: TempFieldConfig[] = [
    {
      type: "text",
      label: "ID",
      name: "id",
      value: user?.id,
      unRender: true,
    },
    {
      type: "text",
      label: t("common.username"),
      name: "username",
      value: user?.username,
    },
    {
      type: "text",
      label: t("common.email"),
      name: "email",
      value: user?.email,
      rules: [
        {
          type: "email",
          message: t("common.emailFormatError"),
        },
      ],
      unRender: true,
    },
    {
      type: "text",
      label: t("common.phoneNumber"),
      name: "phone",
      value: user?.phone,
      rules: [
        {
          pattern: /^\d*$/,
          message: t("common.phoneFormateError"),
        },
      ],
      unRender: true,
    },
    {
      type: "text",
      label: t("user.name"),
      name: "name",
      value: user?.name,
    },
    {
      type: "text",
      label: t("common.nickname"),
      name: "nickname",
      value: user?.nickname,
    },
    {
      type: "select",
      label: t("user.gender"),
      name: "gender",
      value: user?.gender,
      options: [
        { label: t("common.unknow"), value: "U" },
        { label: t("common.man"), value: "M" },
        { label: t("common.female"), value: "W" },
      ],
      customComponent: genderOptions(user?.gender as string),
    },
    {
      type: "datetime",
      label: t("user.birthday"),
      name: "birthdate",
      value: user?.birthdate,
      format: "YYYY/MM/DD",
      style: {
        width: "100%",
      },
      customComponent: moment(user?.birthdate).format("YYYY/MM/DD"),
    },
    {
      type: "text",
      label: t("user.location"),
      name: "address",
      value: user?.address,
    },
    {
      type: "text",
      label: t("common.company"),
      name: "company",
      value: user?.company,
    },
    {
      type: "datetime",
      label: t("common.registerTime"),
      name: "signedUp",
      value: user?.signedUp,
      format: "YYYY/MM/DD",
      style: {
        width: "100%",
      },
      customComponent: moment(user?.signedUp).format("YYYY/MM/DD"),
      unRender: true,
    },
  ];

  const [saveGroup, saveGroupFn] = useAsyncFn(
    async (user: User) => {
      if (isEqual(window.__user__, user)) return;
      const itemKeyArr = fieldConfig.filter((i) => !i.unRender).map((i) => i.name);

      let resParams: { [key: string]: string } = {};
      itemKeyArr.forEach((i) => {
        if (user![i as never] !== window.__user__![i as never]) {
          resParams[i as string] = user![i as never];
        }
      });
      const resUser = await window.__authing__.updateProfile({
        ...resParams,
      });
      window.__user__ = resUser;
      serUser(resUser);

      message.success(t("common.updateSuccess"));
      setEditing(false);
    },
    [user]
  );

  const cardAction = editing ? (
    <>
      <Button type="link" onClick={() => setEditing(false)}>
        {t("common.cancel")}
      </Button>
      <Divider type="vertical" />
      <Button
        loading={saveGroup.loading}
        type="link"
        onClick={() => saveGroupFn(formRef.current?.getFieldsValue())}
        icon={<SaveOutlined />}
      >
        {t("common.save")}
      </Button>
    </>
  ) : (
    <Button
      onClick={() => {
        setEditing(true);
      }}
      type="link"
      icon={<EditOutlined />}
    >
      {t("common.edit")}
    </Button>
  );

  useEffect(() => {
    const deepUser: any = cloneDeep(user);
    deepUser.birthdate = deepUser.birthdate && moment(deepUser.birthdate);

    formRef.current!.setFieldsValue(deepUser);
  }, [user]);

  const unEditItem = useCallback<(item: TempFieldConfig, index: number) => TempFieldConfig>(
    (item: TempFieldConfig, index: number) => ({
      type: "custom",
      label: item.label,
      key: `${item.name}-${index}`,
      customComponent: <div className={styles.inputSpan}>{item.value ? item.customComponent || item.value : "-"}</div>,
      fieldCol: item.fieldCol || {
        span: 12,
      },
    }),
    []
  );

  const mediaEditItem = useCallback(
    (item, index) =>
      item.unReder
        ? null
        : {
            ...item,
            key: `${index}-${item.name}`,
            fieldCol: item.fieldCol || {
              span: 24,
            },
          },
    []
  );

  const mediaUnEditItem = useCallback(
    (item, index) => ({
      type: "custom",
      key: `${item.name}-${index}`,
      customComponent: (
        <div
          className={styles.inputSpan}
          onClick={() => {
            if (!item.unRender) {
              setMediaItemKey(item.name);
              setIsModalVisible(true);
            }
          }}
        >
          <div className={styles.inputSpanHeader}>{item.label}</div>
          <div className={styles.inputSpanFooter}>
            {item.value ? item.customComponent || item.value : ""}
            {item.unRender || <RightOutlined />}
          </div>
        </div>
      ),
    }),
    []
  );

  const editItem = useCallback<(item: TempFieldConfig, index: number) => TempFieldConfig>(
    (item, index) => ({
      ...item,
      key: `${index}-${item.name}`,
      fieldCol: item.fieldCol || {
        span: 12,
      },
    }),
    []
  );

  const defConfig = useMemo(
    () =>
      editing
        ? fieldConfig.map((item, index) => (item.unRender ? unEditItem(item, index) : editItem(item, index)))
        : fieldConfig.map((item, index) => unEditItem(item, index)),
    [editItem, editing, fieldConfig, unEditItem]
  );

  const mediaConfig = useMemo(() => fieldConfig.map((item, index) => mediaUnEditItem(item, index)), [
    fieldConfig,
    mediaUnEditItem,
  ]);

  const useFieldConfig = useMemo(() => (isPhoneMedia ? mediaConfig : defConfig), [
    defConfig,
    isPhoneMedia,
    mediaConfig,
  ]);

  const mediaEditFormReder = useMemo(
    () => fieldConfig.filter((item) => item.name === mediaItemKey)?.map((item, index) => mediaEditItem(item, index)),
    [fieldConfig, mediaEditItem, mediaItemKey]
  );

  return (
    <ConfigCard title={t("common.baseInfo")} extra={isPhoneMedia || cardAction} className={styles.basic}>
      <SimpleForm
        ref={formRef}
        labelAlign="left"
        layout="vertical"
        labelCol={{ span: 24 }}
        useRowLayout={true}
        rowProps={{
          gutter: 36,
        }}
        fieldConfig={useFieldConfig as TempFieldConfig[]}
      />
      <MediaEditForm
        user={user!}
        isModalVisible={isModalVisible}
        handleOkCb={(user: User) => {
          saveGroupFn(user);
          setIsModalVisible(false);
        }}
        handleCancelCb={() => setIsModalVisible(false)}
        renderFieldConfig={mediaEditFormReder as TempFieldConfig[]}
      />
    </ConfigCard>
  );
};
